import { MessageDefinition } from '../../types/message';
import {
  AbsenceMessage,
  ClubParticipantInviteMessage,
  EventInvitationMessage,
  EventParticipationMessage
} from '../../models/message';
import {
  MESSAGE_INVITATION_STATUS,
  MESSAGE_KIND,
  MESSAGE_KIND_SERVER_TO_CLIENT_MAPPING,
  MESSAGE_TYPE
} from '../../consts/message';
import { DATE_FORMAT } from '../../consts/date';
import Moment from 'moment';
import propz from 'propz';

export function getProgress(current: number, total: number): number {
  return (current / total) * 100;
}

export function isAbsenceMessage(message: MessageDefinition): message is AbsenceMessage {
  return (message as any).kind === MESSAGE_KIND.AbsenceMessage;
}

export function isEventInvitationMessage(message: MessageDefinition): message is EventInvitationMessage {
  return (message as any).kind === MESSAGE_KIND.EventInvitationMessage;
}

export function isEventParticipationMessage(message: MessageDefinition): message is EventParticipationMessage {
  return (message as any).kind === MESSAGE_KIND.EventParticipationMessage;
}

export function isClubParticipantInviteMessage(message: MessageDefinition): message is ClubParticipantInviteMessage {
  return (message as any).kind === MESSAGE_KIND.ClubParticipantInviteMessage;
}

export function getStatus(message, type): string {
  let status = '';

  const isMessageTypeArchive = type === MESSAGE_TYPE.ARCHIVE;

  const isMessageKindInvitation = message.kind === MESSAGE_KIND.EventInvitationMessage;
  const isMessageKindConfirmationRequest = message.kind === MESSAGE_KIND.TournamentConfirmationRequestMessage;
  const isMessageKindParticipantInvite = message.kind === MESSAGE_KIND.ClubParticipantInviteMessage;

  const isMessageStatusAccepted = message.invitationStatus === MESSAGE_INVITATION_STATUS.ACCEPTED;
  const isMessageStatusOutdated = message.invitationStatus === MESSAGE_INVITATION_STATUS.OUTDATED;
  const isMessageStatusRejected = message.invitationStatus === MESSAGE_INVITATION_STATUS.REJECTED;

  if (isMessageTypeArchive) {
    switch (true) {
      case isMessageKindInvitation:
      case isMessageKindConfirmationRequest:
        switch (true) {
          case isMessageStatusAccepted:
            status = 'Accepted';
            break;
          case isMessageStatusOutdated:
            status = 'Outdated';
            break;
          case isMessageStatusRejected:
            status = 'Declined';
            break;
        }
        break;
      case isMessageKindParticipantInvite:
        switch (true) {
          case isMessageStatusAccepted:
            status = 'Booked';
            break;
          case isMessageStatusOutdated:
            status = 'Outdated';
            break;
          case isMessageStatusRejected:
            status = 'Declined';
            break;
        }
        break;
    }
  }

  return status;
}

export function transformUIFilters(uiFilters: any): any {
  const transformed = uiFilters.where
    ? JSON.parse(JSON.stringify(uiFilters))
    : { where: JSON.parse(JSON.stringify(uiFilters)) };

  if (transformed.where) {
    if (Object.prototype.hasOwnProperty.call(transformed.where, 'tournamentName')) {
      const tn = transformed.where.tournamentName;
      if (typeof tn === 'string' && tn.trim() === '') {
        delete transformed.where.tournamentName;
      } else if (typeof tn === 'string') {
        const term = tn.trim().replace(/%/g, '');
        transformed.where['tournamentData.name'] = { $regex: `.*${term}.*`, $options: 'i' };
        delete transformed.where.tournamentName;
      } else if (tn.like && typeof tn.like === 'string' && tn.like.trim() !== '') {
        const term = tn.like.trim().replace(/%/g, '');
        transformed.where['tournamentData.name'] = { $regex: `.*${term}.*`, $options: tn.options || 'i' };
        delete transformed.where.tournamentName;
      }
    }
    delete transformed.where.tournamentStartDate;
  }
  return transformed;
}

export function statusChangedAfterFirstResponse(message: any): boolean {
  const firstAt = message?.isActionPerformedSetAt ? new Date(message.isActionPerformedSetAt).getTime() : 0;
  const statusAt = message?.invitationStatusSetAt ? new Date(message.invitationStatusSetAt).getTime() : 0;
  return !!firstAt && statusAt > firstAt;
}

export function getRespondedAt(msg: any): number | null {
  const status = String(msg?.invitationStatus || '').toUpperCase();
  const dt = msg?.firstResponseAt ?? (status && status !== 'NOT_READY' ? msg?.invitationStatusSetAt : null) ?? null;
  return dt ? new Date(dt as any).getTime() : null;
}

export function wasEditedAfterResponse(message: any, epsMs = 1500): boolean {
  const respondedAt = getRespondedAt(message);
  if (!respondedAt) return false;

  const updatedAt = message?.updatedAt ? new Date(message.updatedAt).getTime() : 0;
  if (!updatedAt) return false;

  if (updatedAt - respondedAt <= epsMs) return false;

  const fields = Array.isArray(message?.fields) ? message.fields : [];
  const hasAnyValue = fields.some(
    (f: any) => f && 'value' in f && f.value !== '' && f.value !== null && typeof f.value !== 'undefined'
  );
  return hasAnyValue;
}

export function getDeadlineIndicator(deadline: Date): { className: string; title: string } {
  const now = new Date();
  const msLeft = deadline.getTime() - now.getTime();

  const title = `Deadline: ${deadline.toLocaleDateString()} ${deadline.toLocaleTimeString()}`;

  if (msLeft <= 0) return { className: 'deadline-overdue', title: `${title} (overdue)` };

  const hoursLeft = msLeft / (1000 * 60 * 60);

  if (hoursLeft <= 24) return { className: 'deadline-red', title: `${title} (due within 24h)` };
  if (hoursLeft <= 72) return { className: 'deadline-amber', title: `${title} (due within 3 days)` };
  return { className: 'deadline-green', title };
}
export function getResponseUser(message: any): { fullName: string; email: string } {
  const firstName = String(propz.get(message, ['actionPerformedByData', 'firstName'], '') || '').trim();
  const lastName = String(propz.get(message, ['actionPerformedByData', 'lastName'], '') || '').trim();
  const fullName = String(propz.get(message, ['actionPerformedByData', 'fullName'], `${firstName} ${lastName}`) || '').trim();
  const email = String(propz.get(message, ['actionPerformedByData', 'email'], '') || '').trim();

  return { fullName, email };
}

export function getResponseUserText(message: any): string {
  const { fullName, email } = getResponseUser(message);
  return [fullName, email].filter(Boolean).join('\n');
}

export function formatResponseDateTime(raw: any): string {
  if (!raw) return '';
  const date = Moment(raw);
  return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : '';
}

export function getRespondedAtText(message: any): string {
  const respondedAt = getRespondedAt(message);
  return respondedAt ? formatResponseDateTime(respondedAt) : '';
}

export function getLastResponseUpdatedAtText(message: any): string {
  const updatedAt = propz.get(message, ['updatedAt']);
  return updatedAt ? formatResponseDateTime(updatedAt) : '';
}
